<template>
  <div>
    <el-row>
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="20">
        <div class="title">
          <h1>Prop-based virtual reality storytelling</h1>
        </div>
        <div class="picture project12-head">
          <img src="../assets/VR&P-ST.png.webp" />
        </div>
        <div class="paper_published">
          Gong, Q.*, Wang, Z., Chen, Y., Zou, N. (2024). ‘Rhetoric’ in the design process: The role of prop-based virtual reality storytelling in user-centred product conceptual design. Design Journal, 27(6).（AHCI，唯一一作）
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>

    <el-row :gutter="40" style="margin-top: 50px">
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="10">
        <div class="picture">
          <img src="../assets/project12_1.jpg.webp" />
          <br /><br /><img src="../assets/project12_2.png.webp" />
        </div>
      </el-col>
      <el-col :span="10">
        <div class="sub-title">1 Introduction</div>
        <div class="text">
          User-centred product conceptual design (UCPCD) is a core process focusing on user needs, including user study and product definition. Traditional empathy methods rely on imagination, interviews and storyboards, which lack immersive sensory experience and have limitations in capturing real user scenarios. From the perspective of ‘computing with things’, UCPCD can be regarded as a grammatical process, and prop‑based virtual reality storytelling (VR&amp;P‑ST) can act as a kind of ‘rhetoric’ to optimize this process. By introducing the four characteristics of ‘epistemic objects’ (fidelity, commitment, ambiguity, historicity), this study proposes that VR&amp;P‑ST can improve fidelity, neutralize ambiguity and enhance historicity, so as to enhance designers’ emotional engagement and improve their evaluation and experience of the design process. This study aims to verify the role of VR&amp;P‑ST in UCPCD through empirical experiments, and provide a new immersive empathy method for user‑centred design.
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>

    <el-row :gutter="40" style="margin-top: 50px">
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="10">
        <div class="sub-title">2 Methods</div>
        <div class="text">
          This study adopted a controlled within‑group experimental design, comparing the performance of 15 professional designers in UCPCD under two conditions: with VR&amp;P‑ST and without VR&amp;P‑ST. First, a VR immersive storytelling system was developed based on Unity and HTC VIVE Pro, integrating passive haptics through 3D‑printed modular tangible props that simulate handheld products. The system supports role selection, scenario experience, user story recording, product story creation and cross‑comparison. Four user personas (adult male, adult female, child, elderly) and two scenarios (office, home) were set. During the experiment, participants completed concept design tasks using epistemic object materials (empathy maps, definition forms). Data collection included subjective questionnaires with 10 hypotheses, user demand evaluations from 9 target users, and interview records. Data were analysed by independent sample t‑test and Pearson correlation to verify the hypotheses.
        </div>
      </el-col>
      <el-col :span="10">
        <div class="picture">
          <img src="../assets/project12_3.jpg.webp" />
          <br /><br /><img src="../assets/project12_4.jpg.webp" />
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>

    <el-row :gutter="40" style="margin-top: 50px">
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="10">
        <div class="picture">
          <img src="../assets/project12_5.jpg.webp" />
          <br /><br /><img src="../assets/project12_6.jpg.webp" />
          <br /><br /><img src="../assets/project12_7.png.webp" />
        </div>
      </el-col>
      <el-col :span="10">
        <div class="sub-title">3 Conclusion</div>
        <div class="text">
          This study verifies that VR&amp;P‑ST, as a ‘rhetoric’ in UCPCD, can effectively optimize the conceptual design process. It improves designers’ user understanding, need translation ability and critical thinking, and enhances the fidelity and historicity of epistemic objects while neutralizing ambiguity. Positive emotional engagement is significantly correlated with the above three abilities, and further improves designers’ evaluation of method usability, creative outcomes and UCD knowledge comprehension, but has no significant effect on the evaluation of design process integration. The design outcomes generated by VR&amp;P‑ST are more recognised by most target users. This method breaks the space‑time constraints of traditional user research, reduces costs and improves immersive empathy. Limitations include unstable VR physical properties, prop dependence and limited application scope. Future research can optimise the system, expand prop types and verify the generalizability in more product fields.
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>

    <el-row :gutter="40" style="margin-top: 50px">
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="20">
     
        <div class="text">
          Based on rhetoric theory and the four characteristics of epistemic objects, this study derived three core rhetorical roles of VR&amp;P‑ST and 10 correlated hypotheses:
          <br /><br />
          <b>Metonymy – Improving Fidelity:</b> VR&amp;P‑ST provides consistent visual‑tactile experience, helping designers share sensory experience with users, thus significantly improving user understanding (H1). Higher user understanding enhances designers’ pleasure in the process (H4).
          <br /><br />
          <b>Pun – Neutralizing Ambiguity:</b> The immersive storytelling translates implicit deep user needs into explicit behaviours, significantly improving the ability to translate user needs (H2). Stronger need translation ability promotes positive emotions during design (H5).
          <br /><br />
          <b>Antithesis – Enhancing Historicity:</b> The system visualizes and compares story contexts, stimulating designers to iterate and criticise concepts, significantly improving critical thinking (H3). Better critical thinking is positively related to emotional pleasure (H6).
          <br /><br />
          Furthermore, positive emotional pleasure (joy) in the design process will significantly improve designers’ evaluation of:
          <br /><br />
          Usability of the design method (H7)
          <br />Creative concept generation (H9)
          <br />Understanding of UCD knowledge (H10)
          <br />But has no significant effect on the evaluation of the impact on existing design processes (H8).
          <br /><br />
          The experimental results supported 9 hypotheses and rejected H8, confirming that VR&amp;P‑ST acts as effective rhetoric to improve UCPCD by shaping epistemic objects.
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>

    <el-row :gutter="40" style="margin-top: 50px">
      <el-col :span="2"><div class="grid-content"></div></el-col>
      <el-col :span="20">
        <div class="picture">
          <img src="../assets/project12_9.png.webp" />
        </div>
      </el-col>
      <el-col :span="2"><div class="grid-content"></div></el-col>
    </el-row>
  </div>
</template>

<script>
export default {
  name: "Project12",
};
</script>

<style scoped>
.paper_published {
  margin-top: 24px;
  font-size: 15px;
  color: #333;
  font-weight: 400;
  text-align: justify;
  line-height: 1.9;
  border-bottom: 1px solid #333;
  padding-bottom: 6px;
}
.picture img {
  width: 100%;
}
.picture.project12-head img {
  width: 85%;
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.sub-title {
  margin-bottom: 20px;
  font-size: 18px;
  color: #333;
  font-weight: 400;
  text-align: justify;
}
.text {
  font-size: 15px;
  color: #333;
  font-weight: 100;
  text-align: justify;
  line-height: 1.9;
}
.abstract {
  margin-top: 40px;
  font-size: 15px;
  color: #333;
  font-weight: 100;
  text-align: justify;
}
.grid-content {
  min-height: 36px;
}
.title {
  margin-top: 100px;
  text-align: center;
}
.title h1 {
  font-size: 30px;
  font-weight: 100;
  letter-spacing: 6px;
  color: #333333;
}
</style>